import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { X } from 'lucide-react';

import usePayrollApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import { calculatePayroll, confirmPayroll, lockPayroll } from '../../services/timesheetService';
import api from '../../services/api';

import DataTable from '../../components/ui/DataTable';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import { buildReportLabels } from '../../utils/reportLabels';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import PersonCell from '../../components/ui/PersonCell';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// ─── Styled Components ───────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// AKM POC §5.1 — "fazla mesai sınıflandırması içermez" bandı KALDIRILDI: bordronun fazla mesai sütunu
// artık onaylanan (hesaplanan) fazla mesaidir (payroll.approvedOvertimeMinutes).

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark?.little || '#A0A8B4'};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;
`;

const FieldItem = styled.div`
  grid-column: span ${({ $span }) => $span || 12};
`;

// ─── Helpers ─────────────────────────────────────────────────────

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => {
  const y = currentYear - 5 + i;
  return { id: y, name: String(y) };
});

// ─── Main Component ──────────────────────────────────────────────

export default function PayrollSummaryPage() {
  const { t } = useTranslation();
  const { canRead, canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const {
    fetching, columns, data, pagination, refresh, statusLabels,
  } = usePayrollApi(queryParams);

  const monthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: t(`months.${i}`) })),
    [t]
  );

  // Calculate modal state
  const [calcDrawerOpen, setCalcDrawerOpen] = useState(false);
  const [calcForm, setCalcForm] = useState({ year: null, month: null, employeeIds: [] });
  const [calculating, setCalculating] = useState(false);

  // Confirm / Lock dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, action: null });
  const [actionLoading, setActionLoading] = useState(false);

  // ─── Table handlers ──────────────────────────────────────────────

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // ─── Calculate handlers ──────────────────────────────────────────

  const handleOpenCalc = useCallback(() => {
    setCalcForm({ year: null, month: null, employeeIds: [] });
    setCalcDrawerOpen(true);
  }, []);

  const handleCalculate = useCallback(async () => {
    setCalculating(true);
    try {
      await calculatePayroll({
        year: calcForm.year,
        month: calcForm.month,
        employeeIds: calcForm.employeeIds.length > 0 ? calcForm.employeeIds : undefined,
      });
      setCalcDrawerOpen(false);
      refresh();
    } finally {
      setCalculating(false);
    }
  }, [calcForm, refresh]);

  // ─── Dışa aktarma ────────────────────────────────────────────────
  // AKM POC §7.1/§7.6 — sunucu tarafı Excel / PDF (TAM veri, o anki süzgeçler: yıl, ay, durum, ad ve ortak PDKS süzgeçleri).
  // Eski base64 CSV indirmesi kalktı. Okuma yetkisi (TA.Read) yeter — dosya, listenin gösterdiğinin aynısıdır.

  const getExportLabels = useCallback(() => {
    const extra = {
      unpaidLeaveWorkDays: t('payroll.unpaidLeaveDays'),
      medicalLeaveWorkDays: t('payroll.medicalLeaveDays'),
      holidayDayEquivalent: t('payroll.holidayDays'),
      earlyLeaveMinutes: t('taReports.columns.earlyLeave'),
    };
    Object.entries(statusLabels || {}).forEach(([code, label]) => { extra[`payrollStatus.${code}`] = label; });
    return buildReportLabels(t, columns, t('payroll.title'), extra);
  }, [t, columns, statusLabels]);

  const toolbarActions = useMemo(() => (canRead('TA')
    ? <ReportExportButtons url="/v1/ta/payroll-summary/export" params={queryParams} getLabels={getExportLabels} />
    : null), [canRead, queryParams, getExportLabels]);

  // ─── Confirm / Lock handlers ─────────────────────────────────────

  const handleConfirmAction = useCallback(async () => {
    setActionLoading(true);
    try {
      if (confirmDialog.action === 'confirm') {
        await confirmPayroll(confirmDialog.id);
      } else if (confirmDialog.action === 'lock') {
        await lockPayroll(confirmDialog.id);
      }
      setConfirmDialog({ open: false, id: null, action: null });
      refresh();
    } finally {
      setActionLoading(false);
    }
  }, [confirmDialog, refresh]);

  // ─── Row actions ────────────────────────────────────────────────

  const rowActions = useMemo(() => {
    if (!canWrite('TA')) return [];
    return [
      {
        label: t('payroll.confirm'),
        onClick: (row) => setConfirmDialog({ open: true, id: row.id, action: 'confirm' }),
        visible: (row) => row.status === 'draft',
      },
      {
        label: t('payroll.lock'),
        onClick: (row) => setConfirmDialog({ open: true, id: row.id, action: 'lock' }),
        visible: (row) => row.status === 'confirmed',
      },
    ];
  }, [canWrite, t]);

  // ─── Employee search ─────────────────────────────────────────────

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <Container>

      <HeaderRow>
        <div />
        {canWrite('TA') && (
          <ButtonGroup>
            <Button onClick={handleOpenCalc}>
              {t('payroll.calculate')}
            </Button>
          </ButtonGroup>
        )}
      </HeaderRow>

      {useMemo(() => (
        <DataTable
          title={t('payroll.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          rowActions={rowActions}
          toolbarActions={toolbarActions}
          download={false}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, rowActions, toolbarActions,
      ])}

      {/* Calculate Drawer */}
      <Drawer
        open={calcDrawerOpen}
        onClose={() => setCalcDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('payroll.calculateTitle')}</DrawerTitle>
          <CloseBtn onClick={() => setCalcDrawerOpen(false)}>
            <X size={20} />
          </CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          <FieldGroup>
            <FieldItem $span={6}>
              <GenericSelect
                label={t('payroll.year')}
                options={yearOptions}
                value={calcForm.year}
                onChange={(val) => setCalcForm((p) => ({ ...p, year: val }))}
                required
              />
            </FieldItem>
            <FieldItem $span={6}>
              <GenericSelect
                label={t('payroll.month')}
                options={monthOptions}
                value={calcForm.month}
                onChange={(val) => setCalcForm((p) => ({ ...p, month: val }))}
                required
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AsyncGenericSelect
                label={t('payroll.employees')}
                value={calcForm.employeeIds}
                onChange={(val) => setCalcForm((p) => ({ ...p, employeeIds: val }))}
                onSearch={searchEmployees}
                renderOption={(opt) => (
                  <PersonCell
                    name={opt.name || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()}
                    profilePicture={opt.profilePicture}
                  />
                )}
                isMulti
              />
            </FieldItem>
          </FieldGroup>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => setCalcDrawerOpen(false)}>
            {t('payroll.cancel')}
          </Button>
          <Button
            onClick={handleCalculate}
            disabled={calculating || !calcForm.year || !calcForm.month}
          >
            {calculating ? t('payroll.calculating') : t('payroll.calculate')}
          </Button>
        </DrawerFooter>
      </Drawer>

      {/* Confirm / Lock Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.action === 'confirm' ? t('payroll.confirmTitle') : t('payroll.lockTitle')}
        message={confirmDialog.action === 'confirm' ? t('payroll.confirmMessage') : t('payroll.lockMessage')}
        confirmText={confirmDialog.action === 'confirm' ? t('payroll.confirm') : t('payroll.lock')}
        cancelText={t('payroll.cancel')}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ open: false, id: null, action: null })}
        loading={actionLoading}
        type="info"
      />
    </Container>
  );
}
